import { FC, ChangeEvent, useState } from 'react';
import { format } from 'date-fns';
import numeral from 'numeral';
import PropTypes from 'prop-types';
import {
  Tooltip,
  Divider,
  Box,
  FormControl,
  InputLabel,
  Card,
  Checkbox,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableContainer,
  Select,
  MenuItem,
  Typography,
  useTheme,
  CardHeader,
  Stack,
  TextField,
  Button,
  CircularProgress
} from '@mui/material';
import { Link, NavLink as RouterLink } from 'react-router-dom';
import Label from 'src/components/Label';
import logo from '../../../../../assets/images/PLS.png';
import {
  ContractorStatus,
  CryptoOrder,
  CryptoOrderStatus,
  Contractor,
  JobStatus,
  Job
} from 'src/models/crypto_order';
import EditTwoToneIcon from '@mui/icons-material/EditTwoTone';
import DeleteTwoToneIcon from '@mui/icons-material/DeleteTwoTone';
import AddIcon from '@mui/icons-material/Add';
import { DatePicker, TimePicker, DateTimePicker } from '@mui/lab';
import Avatar from '@mui/material/Avatar';
import { useNavigate } from 'react-router';
import { NavLink } from 'react-router-dom';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import Swal from 'sweetalert2';
import {
  ApprovalSharp,
  Archive,
  DeleteForever,
  PictureAsPdf,
  Restore
} from '@mui/icons-material';
import { changeQuoteStatus, deleteQuote } from 'src/helper/backend_helper';
import moment from 'moment';

// interface RecentOrdersTableProps {
//   className?: string;
//   Job: Job[];
// }

interface Filters {
  status;
}

const getStatusLabel = (quote) => {
  const map = {
    sent: {
      text: 'Sent',
      color: 'primary'
    },
    approved: {
      text: 'Approved',
      color: 'success'
    },
    request: {
      text: 'Request to edit',
      color: 'warning'
    },
    resent: {
      text: 'Edited and sent',
      color: 'secondary'
    },
    declined: {
      text: 'Declined',
      color: 'error'
    },
    draft: {
      text: 'Draft',
      color: 'secondary'
    }
  };
  const { text, color } = map[quote.trim()];

  return <Label color={color}>{text}</Label>;
};

const applyFilters = (Job, filters) => {
  return Job.filter((job) => {
    let matches = true;

    if (filters.status && job.status !== filters.status) {
      matches = false;
    }

    return matches;
  });
};

const statusOptions = [
  {
    id: 'all',
    name: 'All'
  },
  {
    id: 'sent',
    name: 'Sent'
  },
  {
    id: 'approved',
    name: 'Approved'
  },
  {
    id: 'declined',
    name: 'Declined'
  },
  {
    id: 'request',
    name: 'Request to edit'
  },
  {
    id: 'resent',
    name: 'Edited and sent'
  },
  {
    id: 'draft',
    name: 'Draft'
  }
];

const applyPagination = (Job, page: number, limit: number) => {
  return Job.slice(page * limit, page * limit + limit);
};

const QuoteTable = ({ Job }) => {
  const navigate = useNavigate();
  const [selectedCryptoOrders, setSelectedCryptoOrders] = useState([]);
  const selectedBulkActions = selectedCryptoOrders.length > 0;
  const [page, setPage] = useState<number>(0);
  const [limit, setLimit] = useState<number>(25);

  const [filters, setFilters] = useState({
    status: null
  });

  const handlePageChange = (event, newPage: number) => {
    setPage(newPage);
  };

  const handleLimitChange = (event) => {
    setLimit(parseInt(event.target.value));
  };

  const filteredCryptoOrders = applyFilters(Job, filters);
  const paginatedCryptoOrders = applyPagination(
    filteredCryptoOrders,
    page,
    limit
  );

  // const handlePreviewClick = (job) => {
  //   const shifts = job.shiftsHistory.map((shift) => ({
  //     ...shift,
  //     totals: shift.total
  //   }));

  //   const queryString = new URLSearchParams({
  //     positions: JSON.stringify(shifts),
  //     location: job.address,
  //     title: job.title,
  //     billTo: job.billTo,
  //     payTo: job.payTo,
  //     clientPo: job.clientPo,
  //     clientEmail: job.clientEmail,
  //     city: job.city,
  //     adminFee: job.isAdminFee
  //   }).toString();
  //   window.open(`/quote/quoteTemplate?${queryString}`, '_blank');
  // };

  const handlePreviewClick = (job) => {
    const shifts = job.invoiceShifts.map((shift) => ({
      ...shift,
      totals: shift.total
    }));

    const data = {
      companyOwnership: job.companyOwnership ?? job.Event?.companyOwnership,
      positions: shifts,
      location: job.address,
      title: job.title,
      billTo: job.billTo,
      payTo: job.payTo,
      clientPo: job.clientPo,
      clientEmail: job.clientEmail,
      city: job.city,
      adminFee: job.isAdminFee
    };

    sessionStorage.setItem('quoteData', JSON.stringify(data));

    window.open('/quote/invoiceTemplate', '_blank');
  };

  return (
    <Box sx={{ width: '100%', overflow: 'hidden' }}>
      <Divider />

      <TableContainer sx={{ width: '100%' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Job Number</TableCell>
              <TableCell>Client</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created At</TableCell>
              {/* <TableCell>Request Notes</TableCell> */}
              {/* <TableCell>Action / Approved By</TableCell>
              <TableCell>Action / Approved Date</TableCell> */}
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedCryptoOrders.map((job, index) => {
              const isCryptoOrderSelected = selectedCryptoOrders.includes(
                job.id
              );
              return (
                <TableRow
                  hover
                  key={job.id}
                  selected={isCryptoOrderSelected}
                  sx={{
                    cursor: 'pointer'
                  }}
                >
                  <TableCell>
                    <Typography
                      variant="body1"
                      color="text.primary"
                      gutterBottom
                      noWrap
                      onClick={() => {
                        // handlePreviewClick(job);
                        // window.open(`/quote/quoteTemplte`, '_blank');
                      }}
                    >
                      {/* {job.status == 'approved' ? (
                        <Link
                          to={`/dashboards/jobTracker/positions/${job.id}`}
                          state={{ title: job.title }}
                        >
                          {job.title}
                        </Link>
                      ) : ( */}
                      {job.title}
                      {/* )} */}
                    </Typography>
                  </TableCell>
                  <TableCell>{job.billTo}</TableCell>
                  <TableCell>{getStatusLabel(job.status)}</TableCell>

                  <TableCell>
                    {moment(job.createdAt).format('DD MMM, YYYY')}
                  </TableCell>

                  <TableCell>
                    <Tooltip title="Client Invoice Version" arrow>
                      <IconButton
                        onClick={() => {
                          handlePreviewClick(job);
                        }}
                        size="small"
                      >
                        <PictureAsPdf fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Box p={2}>
        <TablePagination
          component="div"
          count={filteredCryptoOrders.length}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleLimitChange}
          page={page}
          rowsPerPage={limit}
          rowsPerPageOptions={[25, 50, 100]}
        />
      </Box>
    </Box>
  );
};

export default QuoteTable;
